import * as React from 'react';
import { useDispatch } from 'react-redux';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Text from 'antd/lib/typography/Text';
import { HolderOutlined } from '@ant-design/icons';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IssueState } from '@/types';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { tryUpdateOrgSettings } from '@/actions/orgSettings';
import { updateTriageStateSortOrderSuccess } from '@/actions/user';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { displayTextForIssueState } from '@/helpers/displayHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import { CLOSED_ISSUE_STATES } from './triageHelpers';

const isSameOrder = (a: IssueState[], b: IssueState[]): boolean =>
    a.length === b.length && a.every((state, i) => state === b[i]);

const isClosed = (state: IssueState) => CLOSED_ISSUE_STATES.includes(state);

const splitByGroup = (
    order: IssueState[] | undefined,
    defaultOrder: IssueState[],
): { open: IssueState[]; closed: IssueState[] } => {
    const base = order?.length ? order : defaultOrder;
    const all = [...base, ...defaultOrder.filter((state) => !base.includes(state))];
    return { open: all.filter((state) => !isClosed(state)), closed: all.filter(isClosed) };
};

const SortableStateRow: React.FC<{ state: IssueState; index: number }> = ({ state, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: state });
    const style: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        border: `1px solid ${antGrey.grey4}`,
        borderRadius: 6,
        backgroundColor: isDragging ? antGrey.grey2 : undefined,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        cursor: 'grab',
        userSelect: 'none',
        ...(isDragging ? { position: 'relative', zIndex: 1 } : {}),
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <HolderOutlined style={{ color: antGrey.grey6 }} />
            <Text type="secondary" style={{ width: 16, textAlign: 'right' }}>
                {index + 1}
            </Text>
            <Text>{displayTextForIssueState(state)}</Text>
        </div>
    );
};

const SortableStateList: React.FC<{
    title: string;
    order: IssueState[];
    onChange: (order: IssueState[]) => void;
}> = ({ title, order, onChange }) => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return;
        onChange(arrayMove(order, order.indexOf(active.id as IssueState), order.indexOf(over.id as IssueState)));
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Text strong>{title}</Text>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                    {order.map((state, index) => (
                        <SortableStateRow key={state} state={state} index={index} />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
};

type EditTriageSortOrderModalProps = {
    open: boolean;
    onCancel: () => void;
    onSaved: () => void;
    orgId: string;
    userId: string;
    currentOrder?: IssueState[];
    defaultOrder: IssueState[];
};

export const EditTriageSortOrderModal: React.FC<EditTriageSortOrderModalProps> = ({
    open,
    onCancel,
    onSaved,
    orgId,
    userId,
    currentOrder,
    defaultOrder,
}) => {
    const dispatch = useDispatch();
    const [openOrder, setOpenOrder] = React.useState<IssueState[]>(() => splitByGroup(currentOrder, defaultOrder).open);
    const [closedOrder, setClosedOrder] = React.useState<IssueState[]>(
        () => splitByGroup(currentOrder, defaultOrder).closed,
    );
    const order = React.useMemo(() => [...openOrder, ...closedOrder], [openOrder, closedOrder]);
    const isDefault = isSameOrder(order, defaultOrder);
    const resetTo = React.useCallback(
        (source: IssueState[] | undefined) => {
            const { open: nextOpen, closed: nextClosed } = splitByGroup(source, defaultOrder);
            setOpenOrder(nextOpen);
            setClosedOrder(nextClosed);
        },
        [defaultOrder],
    );

    React.useEffect(() => {
        if (open) resetTo(currentOrder);
    }, [open, currentOrder, resetTo]);

    const isLoading = useIsLoading(OrgSettingsUpdatable.UpdateTriageStateSortOrderForOrgUser);
    const isUpdated = useUpdated(OrgSettingsUpdatable.UpdateTriageStateSortOrderForOrgUser);
    const updateError = useError(OrgSettingsUpdatable.UpdateTriageStateSortOrderForOrgUser);

    // `isUpdated` stays true for a short window; only react to its rising edge.
    const wasUpdated = React.useRef(false);
    React.useEffect(() => {
        if (isUpdated && !wasUpdated.current) {
            dispatch(updateTriageStateSortOrderSuccess(isDefault ? [] : order));
            globalMessage.success('Triage sort order updated');
            onSaved();
            onCancel();
        }
        wasUpdated.current = isUpdated;
    }, [isUpdated, dispatch, isDefault, order, onSaved, onCancel]);

    React.useEffect(() => {
        if (updateError) globalMessage.error(updateError);
    }, [updateError]);

    const save = () => {
        // An empty list clears the preference so the backend default applies.
        const triageStateSortOrder = isDefault ? [] : order;
        dispatch(
            tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateTriageStateSortOrderForOrgUser, {
                orgId,
                userId,
                triageStateSortOrder,
            }),
        );
    };

    return (
        <Modal
            title="Customize triage sort order"
            open={open}
            onCancel={onCancel}
            onOk={save}
            okText="Save"
            okButtonProps={{ loading: isLoading }}
            width={480}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Text type="secondary">
                    Drag statuses to choose how issues are ordered when you sort by Triage. Issues with the same status
                    show newest first. This order is saved just for you, but it&apos;s included in the URL when you
                    share a Triage page with others.
                </Text>
                <SortableStateList title="Open issues" order={openOrder} onChange={setOpenOrder} />
                <SortableStateList title="Closed issues" order={closedOrder} onChange={setClosedOrder} />
                <div>
                    <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        disabled={isDefault}
                        onClick={() => resetTo(undefined)}
                    >
                        Reset to default
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
